import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { User, Lock, Save, Bell, Send, Landmark, Building2, Link2, FileText, CreditCard, ExternalLink, CheckCircle, Loader2, Sun, Moon, Monitor, Palette, AlertTriangle, XCircle, RotateCcw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import Logo from './Logo';
import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user, token, refreshUser } = useAuth();
  const { preference, setTheme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [profile, setProfile] = useState({ full_name: user?.full_name || '', business_name: user?.business_name || '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/subscription`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setSubscription(res.data)).catch(() => {});
  }, [token]);

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/payments/subscription/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSubscription(s => ({ ...s, cancel_at_period_end: true, deletion_scheduled_at: res.data.deletion_scheduled_at }));
      setShowCancelConfirm(false);
      toast({ title: 'Subscription cancelled', description: `Your account will be deleted on ${new Date(res.data.deletion_scheduled_at).toLocaleDateString()}.` });
    } catch (err) {
      toast({ title: 'Failed to cancel', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setCancelling(false); }
  };

  const handleReactivate = async () => {
    try {
      await axios.post(`${API}/payments/subscription/reactivate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSubscription(s => { const c = { ...s }; delete c.cancel_at_period_end; delete c.deletion_scheduled_at; return c; });
      toast({ title: 'Subscription reactivated', description: 'Your account will not be deleted. Welcome back!' });
    } catch (err) {
      toast({ title: 'Failed to reactivate', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  // Stripe Connect removed — invoicing uses external payment links / bank transfer

  // Reminder Settings
  const [reminderSettings, setReminderSettings] = useState({ auto_reminders_enabled: true, reminder_intervals: [3, 7, 14], custom_message: '' });
  const [savingReminders, setSavingReminders] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState({
    email_enabled: true, notify_missed_calls: true, notify_new_leads: true,
    notify_appointments: true, notify_call_summaries: true, notification_email: ''
  });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Payment Settings
  const [paySettings, setPaySettings] = useState({
    business_name: '', business_email: '', business_phone: '', business_address: '',
    account_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '', iban: '',
    payment_link: '', payment_link_label: '',
    default_currency: 'USD', default_payment_terms: '', default_notes: '', default_tax_rate: 0
  });
  const [savingPay, setSavingPay] = useState(false);

  useEffect(() => {
    if (token) {
      fetchNotificationPrefs();
      fetchPaymentSettings();
      fetchReminderSettings();
    }
  }, [token]);

  const fetchReminderSettings = async () => {
    try {
      const res = await axios.get(`${API}/reminders/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setReminderSettings(res.data);
    } catch {}
  };

  const handleSaveReminderSettings = async () => {
    setSavingReminders(true);
    try {
      await axios.put(`${API}/reminders/settings`, reminderSettings, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Reminder settings saved' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally { setSavingReminders(false); }
  };

  const toggleInterval = (day) => {
    setReminderSettings(prev => {
      const intervals = prev.reminder_intervals.includes(day)
        ? prev.reminder_intervals.filter(d => d !== day)
        : [...prev.reminder_intervals, day].sort((a, b) => a - b);
      return { ...prev, reminder_intervals: intervals };
    });
  };

  const fetchNotificationPrefs = async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifPrefs(prev => ({ ...prev, ...res.data }));
    } catch { }
  };

  const fetchPaymentSettings = async () => {
    try {
      const res = await axios.get(`${API}/payment-settings/`, { headers: { Authorization: `Bearer ${token}` } });
      setPaySettings(prev => ({ ...prev, ...res.data }));
    } catch { }
  };

  const handleNotifSave = async () => {
    setSavingNotifs(true);
    try {
      await axios.put(`${API}/notifications/preferences`, notifPrefs, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Notification preferences saved' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally { setSavingNotifs(false); }
  };

  const [sendingTestAll, setSendingTestAll] = useState(false);

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/notifications/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: res.data.message });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send test email', variant: 'destructive' });
    } finally { setSendingTest(false); }
  };

  const handleTestAll = async () => {
    setSendingTestAll(true);
    try {
      const res = await axios.post(`${API}/notifications/test-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const r = res.data.results;
      const passed = Object.values(r).filter(Boolean).length;
      toast({ title: `${passed}/4 notifications sent`, description: `Call Summary: ${r.call_summary ? 'Sent' : 'Failed'}, New Lead: ${r.new_lead ? 'Sent' : 'Failed'}, Appointment: ${r.appointment ? 'Sent' : 'Failed'}, Missed Call: ${r.missed_call ? 'Sent' : 'Failed'}` });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send test notifications', variant: 'destructive' });
    } finally { setSendingTestAll(false); }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/update-profile`, profile, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Profile updated successfully' });
      if (refreshUser) refreshUser();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to update', variant: 'destructive' });
    } finally { setSavingProfile(false); }
  };

  const handlePasswordChange = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast({ title: 'Passwords do not match', variant: 'destructive' }); return;
    }
    if (passwords.new_password.length < 6) {
      toast({ title: 'Password must be at least 6 characters', variant: 'destructive' }); return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwords.current_password, new_password: passwords.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Password changed successfully' });
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to change password', variant: 'destructive' });
    } finally { setSavingPassword(false); }
  };

  const handlePaySettingsSave = async () => {
    setSavingPay(true);
    try {
      await axios.put(`${API}/payment-settings/`, paySettings, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Payment settings saved', description: 'Your bank details and defaults will auto-fill into new invoices.' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally { setSavingPay(false); }
  };

  const payField = (key, label, placeholder, type = 'text') => (
    <div>
      <Label className="text-gray-400 text-xs">{label}</Label>
      <Input
        type={type}
        value={paySettings[key] || ''}
        onChange={(e) => setPaySettings(p => ({ ...p, [key]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
        placeholder={placeholder}
        className="bg-black/30 border-white/10 text-white mt-1 h-9 text-sm"
        data-testid={`pay-${key}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="settings-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Logo linkTo="/dashboard" />
          <div className="h-6 w-px bg-white/10" />
          <h1 className="text-xl font-bold text-white">Settings</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Profile */}
        <Card className="bg-gray-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" /> Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-400 text-sm">Email</Label>
              <Input value={user?.email || ''} disabled className="bg-black/30 border-white/10 text-gray-500 mt-1" data-testid="settings-email" />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Full Name</Label>
              <Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" data-testid="settings-name" />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Business Name</Label>
              <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" data-testid="settings-business" />
            </div>
            <Button onClick={handleProfileSave} disabled={savingProfile} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" /> {savingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="bg-gray-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Palette className="w-5 h-5 text-purple-400" /> Appearance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400 text-sm mb-4">Choose how Miadesk looks. "System" follows your device preference.</p>
            <div className="grid grid-cols-3 gap-3" data-testid="theme-selector">
              {[
                { value: 'light', label: 'Light', icon: Sun },
                { value: 'dark', label: 'Dark', icon: Moon },
                { value: 'system', label: 'System', icon: Monitor },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  data-testid={`theme-${value}-btn`}
                  className={`flex flex-col items-center gap-2 py-4 rounded-xl border text-sm font-medium transition-all
                    ${preference === value
                      ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                      : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20 hover:text-white'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Payment & Invoicing Settings */}
        <Card className="bg-gray-900/50 border-white/10" data-testid="payment-settings-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Landmark className="w-5 h-5 text-emerald-400" /> Payment & Invoicing
            </CardTitle>
            <p className="text-gray-400 text-sm mt-1">
              Set up your payment details and defaults. These will auto-fill into every new invoice you create.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Business Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-purple-400" /> Business Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {payField('business_name', 'Business Name', 'Acme Inc.')}
                {payField('business_email', 'Business Email', 'billing@acme.com')}
                {payField('business_phone', 'Phone', '+1 (555) 123-4567')}
              </div>
              <div className="mt-3">
                {payField('business_address', 'Address', '123 Main St, New York, NY 10001')}
              </div>
            </div>

            {/* Invoice Defaults */}
            <div>
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-amber-400" /> Invoice Defaults
              </h3>
              <p className="text-xs text-gray-500 mb-3">Set defaults that auto-fill into every new invoice.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-400 text-xs">Default Currency</Label>
                  <select
                    value={paySettings.default_currency || 'USD'}
                    onChange={(e) => setPaySettings(p => ({ ...p, default_currency: e.target.value }))}
                    className="w-full mt-1 h-9 rounded-md bg-black/30 border border-white/10 text-white text-sm px-3"
                    data-testid="pay-default_currency"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="AUD">AUD (A$)</option>
                  </select>
                </div>
                {payField('default_tax_rate', 'Default Tax Rate (%)', '0', 'number')}
              </div>
              <div className="mt-3">
                <Label className="text-gray-400 text-xs">Default Payment Terms</Label>
                <Textarea
                  value={paySettings.default_payment_terms || ''}
                  onChange={(e) => setPaySettings(p => ({ ...p, default_payment_terms: e.target.value }))}
                  placeholder="e.g. Payment due within 30 days of invoice date."
                  rows={2}
                  className="bg-black/30 border-white/10 text-white mt-1 text-sm"
                  data-testid="pay-default_payment_terms"
                />
              </div>
              <div className="mt-3">
                <Label className="text-gray-400 text-xs">Default Notes</Label>
                <Textarea
                  value={paySettings.default_notes || ''}
                  onChange={(e) => setPaySettings(p => ({ ...p, default_notes: e.target.value }))}
                  placeholder="e.g. Thank you for your business!"
                  rows={2}
                  className="bg-black/30 border-white/10 text-white mt-1 text-sm"
                  data-testid="pay-default_notes"
                />
              </div>
            </div>

            <Button onClick={handlePaySettingsSave} disabled={savingPay} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="save-payment-settings-btn">
              <Save className="w-4 h-4 mr-2" /> {savingPay ? 'Saving...' : 'Save Payment Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Invoice Reminder Settings */}
        <Card className="bg-gray-900/50 border-white/10" data-testid="reminder-settings-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-400" /> Invoice Reminders
            </CardTitle>
            <p className="text-gray-400 text-sm mt-1">
              Automatically send payment reminders for overdue invoices via email.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-300 text-sm font-medium">Auto-Reminders</p>
                <p className="text-gray-500 text-xs">Automatically email customers when invoices are overdue</p>
              </div>
              <Switch
                checked={reminderSettings.auto_reminders_enabled}
                onCheckedChange={(v) => setReminderSettings(prev => ({ ...prev, auto_reminders_enabled: v }))}
                data-testid="auto-reminders-toggle"
              />
            </div>

            {reminderSettings.auto_reminders_enabled && (
              <>
                <div>
                  <Label className="text-gray-300 text-sm">Send reminders at</Label>
                  <div className="flex gap-2 mt-2">
                    {[3, 5, 7, 10, 14, 21, 30].map(day => (
                      <button
                        key={day}
                        onClick={() => toggleInterval(day)}
                        data-testid={`interval-${day}`}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                          reminderSettings.reminder_intervals.includes(day)
                            ? 'bg-orange-500/20 border-orange-500/30 text-orange-300 font-medium'
                            : 'border-white/10 text-gray-500 hover:border-white/20'
                        }`}
                      >
                        {day}d
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-500 text-[10px] mt-1.5">Days after due date. Currently: {reminderSettings.reminder_intervals.join(', ')} days overdue</p>
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Custom Message (optional)</Label>
                  <Textarea
                    value={reminderSettings.custom_message}
                    onChange={e => setReminderSettings(prev => ({ ...prev, custom_message: e.target.value }))}
                    placeholder="Add a personal message to your reminder emails..."
                    className="bg-black/30 border-white/10 text-white mt-1 h-20"
                    data-testid="reminder-custom-message"
                  />
                </div>
              </>
            )}

            <Button onClick={handleSaveReminderSettings} disabled={savingReminders} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-reminders-btn">
              <Save className="w-4 h-4 mr-2" /> {savingReminders ? 'Saving...' : 'Save Reminder Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Password */}
        <Card className="bg-gray-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-teal-400" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-400 text-sm">Current Password</Label>
              <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" data-testid="current-password" />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">New Password</Label>
              <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" data-testid="new-password" />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Confirm New Password</Label>
              <Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" data-testid="confirm-password" />
            </div>
            <Button onClick={handlePasswordChange} disabled={savingPassword} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="change-password-btn">
              <Lock className="w-4 h-4 mr-2" /> {savingPassword ? 'Updating...' : 'Change Password'}
            </Button>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="bg-gray-900/50 border-white/10" data-testid="notification-settings">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-400" /> Email Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-gray-400 text-sm">Notification Email (leave blank to use account email)</Label>
              <Input value={notifPrefs.notification_email || ''} onChange={(e) => setNotifPrefs({ ...notifPrefs, notification_email: e.target.value })} placeholder={user?.email || 'your@email.com'} className="bg-black/30 border-white/10 text-white mt-1" data-testid="notification-email" />
            </div>
            <div className="space-y-3">
              {[
                { key: 'email_enabled', label: 'Enable email notifications', desc: 'Master toggle for all email alerts' },
                { key: 'notify_call_summaries', label: 'Call summaries', desc: 'Receive a summary after each call' },
                { key: 'notify_new_leads', label: 'New leads', desc: 'Get notified when Mia captures a lead' },
                { key: 'notify_appointments', label: 'Appointments', desc: 'Notifications for new bookings' },
                { key: 'notify_missed_calls', label: 'Missed calls', desc: 'Alert when a call couldn\'t be handled' }
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                  <div>
                    <p className="text-white text-sm font-medium">{item.label}</p>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                  <Switch checked={notifPrefs[item.key]} onCheckedChange={(checked) => setNotifPrefs({ ...notifPrefs, [item.key]: checked })} data-testid={`toggle-${item.key}`} />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleNotifSave} disabled={savingNotifs} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-notifs-btn">
                <Save className="w-4 h-4 mr-2" /> {savingNotifs ? 'Saving...' : 'Save Preferences'}
              </Button>
              <Button onClick={handleSendTest} disabled={sendingTest} variant="outline" className="border-white/10 text-gray-300 hover:text-white hover:bg-white/5" data-testid="send-test-email-btn">
                <Send className="w-4 h-4 mr-2" /> {sendingTest ? 'Sending...' : 'Send Test Email'}
              </Button>
              <Button onClick={handleTestAll} disabled={sendingTestAll} variant="outline" className="border-amber-500/30 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid="test-all-channels-btn">
                <Send className="w-4 h-4 mr-2" /> {sendingTestAll ? 'Sending 4 emails...' : 'Test All Channels'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subscription / Danger Zone */}
        <Card className="bg-gray-900/50 border-red-500/20" data-testid="subscription-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-red-400" /> Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscription && (
              <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium capitalize">{subscription.plan_type || 'No'} plan</p>
                  <p className="text-gray-500 text-xs capitalize">Status: {subscription.status}</p>
                </div>
                {subscription.cancel_at_period_end && (
                  <span className="text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">Cancellation scheduled</span>
                )}
              </div>
            )}

            {subscription?.cancel_at_period_end ? (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg space-y-3" data-testid="cancellation-scheduled-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-red-300 text-sm">
                    Your subscription is cancelled. Your account and all data will be <span className="font-semibold">permanently deleted on {subscription.deletion_scheduled_at ? new Date(subscription.deletion_scheduled_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'the end of your billing period'}</span>, and your phone number will be returned to Twilio.
                  </p>
                </div>
                <Button onClick={handleReactivate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reactivate-subscription-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reactivate Subscription
                </Button>
              </div>
            ) : !showCancelConfirm ? (
              <div>
                <Button onClick={() => setShowCancelConfirm(true)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="cancel-subscription-btn">
                  <XCircle className="w-4 h-4 mr-2" /> Cancel Subscription
                </Button>
                <p className="text-gray-500 text-xs mt-2">You keep full access until the end of your current billing period.</p>
              </div>
            ) : (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg space-y-3" data-testid="cancel-confirm-panel">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-300 space-y-1">
                    <p className="font-semibold text-red-200">Are you sure? This cannot be undone after the period ends.</p>
                    <ul className="list-disc list-inside text-xs space-y-0.5 text-red-300/90">
                      <li>You keep access until the end of your current subscription month</li>
                      <li>Then your account and ALL data (contacts, calls, invoices, chats) are permanently deleted</li>
                      <li>Your AI phone number is automatically returned to Twilio</li>
                    </ul>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCancelSubscription} disabled={cancelling} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-btn">
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />} Yes, Cancel My Subscription
                  </Button>
                  <Button onClick={() => setShowCancelConfirm(false)} variant="outline" className="border-white/10 text-gray-300" data-testid="keep-subscription-btn">
                    Keep My Subscription
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Settings;
